/**
 * Shared motion vocabulary for the landing page.
 *
 * Every anime.js animation in src/components/landing/* should pull its
 * durations, easings, and stagger spacings from these constants so the
 * page reads as one coherent motion language instead of 13 dialects.
 *
 * v4 API notes:
 *  - Easings are bare strings (e.g. 'outQuint'), no 'easing.' prefix.
 *  - createSpring() returns a Spring instance that can be passed to
 *    anime's `ease` field anywhere a string easing is accepted.
 */

import { createSpring, type Spring } from "animejs";

/**
 * Duration vocabulary in milliseconds.
 *
 * - instant: micro-feedback (button tap, focus ring)
 * - fast: hover/leave transitions, small reveals
 * - base: the default for most enter animations
 * - slow: hero headline, key reveals — lets the eye land before motion finishes
 * - scene: orchestrated multi-step scenes (Hero, ThreeDoors timelines)
 */
export const DURATION = {
  instant: 120,
  fast: 240,
  base: 420,
  slow: 680,
  scene: 1200,
} as const;

/**
 * Easing vocabulary — anime.js v4 string identifiers.
 *
 * - soft: gentle decel, default for content reveals
 * - snap: sharp arrival, good for cards and CTAs
 * - glide: long even arcs, for sliding/scroll-linked motion
 * - tech: precise, machined feel — used by data widgets and code blocks
 */
export const EASE = {
  soft: "outCubic",
  snap: "outExpo",
  glide: "inOutQuart",
  tech: "outQuint",
} as const;

/**
 * Stagger spacings in milliseconds. Pass to anime's stagger() helper or
 * compute manually as `index * STAGGER.base` for sequential reveals.
 *
 * - tight: list items, table rows
 * - base: card grids
 * - loose: hero headline lines
 * - dramatic: marquee/scene establishing shots
 */
export const STAGGER = {
  tight: 30,
  base: 60,
  loose: 100,
  dramatic: 140,
} as const;

/**
 * Mellow spring — settles smoothly, no overshoot. Good for content
 * cards and panels that should feel grounded.
 */
export const SPRING_BASE: Spring = createSpring({
  mass: 1,
  stiffness: 110,
  damping: 14,
});

/**
 * Snappy spring — quick arrival with a small bounce. Use for CTA
 * presses, tooltips, and elements that want to feel responsive.
 */
export const SPRING_SNAPPY: Spring = createSpring({
  mass: 0.8,
  stiffness: 180,
  damping: 12,
});

/** Convenience aliases for callers that want named types. */
export type DurationKey = keyof typeof DURATION;
export type EaseKey = keyof typeof EASE;
export type StaggerKey = keyof typeof STAGGER;
